"use client";
import { PerfectCorner } from "@/component/perfect-corner";
import { useEffect, useRef, useState } from "react";
import * as THREE from "three";
import { ModelPreview } from "./model-preview";
import { useMobileDetection } from "./use-mobile-detection";

const logoSVG = `<?xml version="1.0" encoding="UTF-8"?>
<svg width="27px" height="18px" viewBox="0 0 27 18" version="1.1" xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink">
    <title>编组 2</title>
    <g id="Juchats-Artifacts-Share" stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
        <g id="画板" transform="translate(-955, -534)">
            <g id="编组" transform="translate(763, 512)">
                <g id="编组-2" transform="translate(192, 22)">
                    <polygon id="路径" fill="#FFFFFF" fill-rule="nonzero" points="6.00004243 9.00012728 0 3.00002121 3.00006364 0 12 9.00012728 3.00006364 18.0000636 0 15.0000849"></polygon>
                    <rect id="矩形" fill="#FF1616" x="15" y="13" width="12" height="4.66666667"></rect>
                </g>
            </g>
        </g>
    </g>
</svg>`;

interface ArtifactLoadingProps {
  svgData?: string;
  children?: React.ReactNode;
  maxDistance?: number;
  targetX?: number;
  autoRotate?: boolean;
  user?: {
    avatar: string;
    name: string;
  };
}

interface MaterialPreset {
  name: string;
  label: string;
  roughness: number;
  metalness: number;
  clearcoat: number;
  transmission: number;
  envMapIntensity: number;
}

const MATERIAL_PRESETS: MaterialPreset[] = [
  {
    name: "metallic",
    label: "Metallic",
    roughness: 0.2,
    metalness: 0.9,
    clearcoat: 1.0,
    transmission: 0,
    envMapIntensity: 1.8,
  },
  {
    name: "clay",
    label: "Clay/Matte",
    roughness: 1.0,
    metalness: 0.0,
    clearcoat: 0.0,
    transmission: 0,
    envMapIntensity: 0.3,
  },
  {
    name: "plastic",
    label: "Plastic",
    roughness: 0.4,
    metalness: 0.0,
    clearcoat: 0.6,
    transmission: 0,
    envMapIntensity: 0.8,
  },
  {
    name: "glass",
    label: "Glass",
    roughness: 0.05,
    metalness: 0.0,
    clearcoat: 1.0,
    transmission: 0.95,
    envMapIntensity: 3.5,
  },
  {
    name: "custom",
    label: "Custom",
    roughness: 0.3,
    metalness: 0.5,
    clearcoat: 0,
    transmission: 0,
    envMapIntensity: 1.0,
  },
];

const DARK_MODE_COLOR = "#121212";
const LIGHT_MODE_COLOR = "#f5f5f5";

export function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value);

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value);
    }, delay);

    return () => {
      clearTimeout(handler);
    };
  }, [value, delay]);

  return debouncedValue;
}

const MaxUserName = 8;
const ShowDuration = 3;

const ArtifactLoading = ({ svgData, user, children, maxDistance, targetX, autoRotate }: ArtifactLoadingProps) => {
  const textRef = useRef<HTMLDivElement | null>(null);
  const modelRef = useRef<THREE.Group | null>(null);
  const modelGroupRef = useRef<THREE.Group | null>(null);
  const [modelRotationY, setModelRotationY] = useState<number>(0);
  const [bevelEnabled, setBevelEnabled] = useState<boolean>(true);
  const [bevelThickness, setBevelThickness] = useState<number>(1.0);
  const [bevelSize, setBevelSize] = useState<number>(0.5);
  const [bevelSegments, setBevelSegments] = useState<number>(4);
  const [bevelPreset, setBevelPreset] = useState<string>("medium");
  const [isHollowSvg, setIsHollowSvg] = useState<boolean>(false);
  const [customColor, setCustomColor] = useState<string>("#3498db");
  const [useCustomColor, setUseCustomColor] = useState<boolean>(false);

  const initialPreset = MATERIAL_PRESETS.find((p) => p.name === "metallic") || MATERIAL_PRESETS[0];
  const [roughness, setRoughness] = useState<number>(initialPreset.roughness);
  const [metalness, setMetalness] = useState<number>(initialPreset.metalness);
  const [clearcoat, setClearcoat] = useState<number>(initialPreset.clearcoat);
  const [transmission, setTransmission] = useState<number>(initialPreset.transmission);
  const [envMapIntensity, setEnvMapIntensity] = useState<number>(initialPreset.envMapIntensity);
  const [backgroundColor, setBackgroundColor] = useState<string>(DARK_MODE_COLOR);
  const [useEnvironment, setUseEnvironment] = useState<boolean>(true);
  const [environmentPreset, setEnvironmentPreset] = useState<string>("city");
  const [customHdriUrl, setCustomHdriUrl] = useState<string | null>(null);
  const [autoRotateSpeed, setAutoRotateSpeed] = useState<number>(8);
  const [useBloom, setUseBloom] = useState<boolean>(false);
  const [bloomIntensity, setBloomIntensity] = useState<number>(1.0);
  const [bloomMipmapBlur, setBloomMipmapBlur] = useState<boolean>(true);
  const [vibeModeOriginalMaterial, setVibeModeOriginalMaterial] = useState<string | null>(null);
  const [isModelLoading, setIsModelLoading] = useState<boolean>(false);
  const [svgProcessingError, setSvgProcessingError] = useState<string | null>(null);
  const [showChildren, setShowChildren] = useState(false);

  const { isMobile, continueOnMobile, handleContinueOnMobile, clearMobilePreference } = useMobileDetection();
  const debouncedSvgData = useDebounce(svgData, 300);

  useEffect(() => {
    if (!debouncedSvgData) return;
    const hasClosedPath = debouncedSvgData.includes("Z") || debouncedSvgData.includes("z");
    const hasMultiplePaths = (debouncedSvgData.match(/<path/g) || []).length > 1;
    const hasCircles = debouncedSvgData.includes("<circle");
    const hasEllipse = debouncedSvgData.includes("<ellipse");
    const hasRect = debouncedSvgData.includes("<rect");

    const isLikelyHollow =
      (hasClosedPath && (hasMultiplePaths || hasCircles || hasEllipse || hasRect)) ||
      debouncedSvgData.toLowerCase().includes("smile") ||
      debouncedSvgData.toLowerCase().includes("face");

    setIsHollowSvg(isLikelyHollow);
  }, [debouncedSvgData]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowChildren(true);
    }, 5000);
    return () => {
      clearTimeout(timer);
    };
  }, []);

  if (isModelLoading) {
    return <div>Loading...</div>;
  }

  if (svgProcessingError) {
    return <div>Error: {svgProcessingError}</div>;
  }

  if (showChildren) {
    return children;
  }

  return (
    <div className="size-full absolute left-0 top-0 bg-[#121212] overflow-hidden">
      {user && (
        <div className="absolute left-0 top-0 size-full flex justify-center items-center text-white">
          <div className="inline-flex items-center font-lexend" ref={textRef}>
            <div className="fade-in flex items-center">
              <PerfectCorner src={user.avatar} asLoading />
              <span className="ml-[12px] md:ml-[16px] text-[16px] md:text-[24px] font-bold">
                {user.name.slice(0, MaxUserName)}
                {user.name.length > MaxUserName ? "..." : ""}
              </span>
            </div>
            <span className="ml-[8px] md:ml-[10px] translate-y-[2px]">｜</span>
            <div className="min-w-[36px] w-[36px] h-[28px] relative">
              <ModelPreview
                svgData={svgData || logoSVG}
                depth={1}
                modelRotationY={modelRotationY}
                modelGroupRef={modelGroupRef}
                modelRef={modelRef}
                // Geometry settings
                bevelEnabled={bevelEnabled}
                bevelThickness={bevelThickness}
                bevelSize={bevelSize}
                bevelSegments={bevelSegments}
                isHollowSvg={isHollowSvg}
                spread={0}
                // Material settings
                useCustomColor={useCustomColor}
                customColor={customColor}
                roughness={roughness}
                metalness={metalness}
                clearcoat={clearcoat}
                transmission={transmission}
                envMapIntensity={envMapIntensity}
                // Environment settings
                backgroundColor={backgroundColor}
                useEnvironment={useEnvironment}
                environmentPreset={environmentPreset}
                customHdriUrl={customHdriUrl}
                // Rendering options
                autoRotate={Boolean(autoRotate)}
                autoRotateSpeed={autoRotateSpeed}
                useBloom={useBloom}
                bloomIntensity={bloomIntensity}
                maxDistance={maxDistance}
                bloomMipmapBlur={bloomMipmapBlur}
                isMobile={isMobile}
                targetX={targetX}
                onLoadStart={() => setIsModelLoading(true)}
                onLoadComplete={() => {
                  setTimeout(() => {
                    setShowChildren(true);
                  }, ShowDuration * 1000);
                  setIsModelLoading(false);
                }}
                onError={(error) => {
                  setSvgProcessingError(error.message || "Failed to process SVG");
                  setIsModelLoading(false);
                }}
              />
            </div>
            <span className="text-[12px] md:text-[20px] whitespace-nowrap shark-animation">Artifacts Preview</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ArtifactLoading;
